import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Typography, Box } from '@mui/material';
import MaintenanceDataTable, { MaintenanceColumn } from '../components/MaintenanceDataTable';
import { useNotification } from '../providers/NotificationProvider';

export default function HozenallNetProjectsManagement() {

  const notify = useNotification();
  const [response, setResponse] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ページング状態
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // 開発中に変更しやすいよう、ここに構成を定義します
  const columns: MaintenanceColumn[] = [
    { field: 'anken_no', headerName: '案件番号', width: 100 },
    { field: 'uketsuke_date', headerName: '受付日', width: 80, valueFormatter: (v) => v?.substring(0, 10) },
    { field: 'bukken_name', headerName: '物件名', width: 300 },
    { field: 'biko', headerName: '内容/備考', width: 400 },
    { field: 'status_name', headerName: 'ステータス', width: 120 },
    { field: 'tanto_name', headerName: '担当者', width: 150 },
    {
      field: 'construction_type',
      headerName: '工事内容',
      width: 200,
      editable: true,
      type: 'select',
      options: [
        '緊急対応',
        '小修繕（50万円未満）',
        '小修繕（50万円以上・複数業者）',
        '原状回復',
        '提案工事',
        '法定是正'
      ]
    },
    {
      field: 'on_site_survey_date',
      headerName: '現調実施日',
      width: 100,
      editable: true,
      type: 'date',
      valueFormatter: (v) => v ? v.substring(0, 10) : ''
    },
  ];

  // GCP中継エンドポイント (VPCコネクタ+固定IP経由でオンプレへ直接アクセス。旧Heteml中継の置き換え)
  const BACKEND_API_URL = 'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/hozenallnet_projects';

  // リクエスト実行関数
  const handlePostRequest = useCallback(async (targetPage: number, targetSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        Process: 'Search',
        LIMIT: targetSize,
        OFFSET: targetPage * targetSize,
        limit: targetSize,
        offset: targetPage * targetSize,
      };

      const result = await axios.post(BACKEND_API_URL, payload, {
        timeout: 20000, // コールドスタート分の余裕を持たせる
        headers: {
          'Content-Type': 'application/json',
        }
      });

      let rawData = result.data;

      if (typeof rawData === 'string') {
        const trimmed = rawData.trim();
        const startIndex = trimmed.search(/[\[\{]/);
        if (startIndex !== -1) {
          try {
            rawData = JSON.parse(trimmed.substring(startIndex));
          } catch (e) {
            console.warn('JSONパース失敗:', e);
          }
        }
      }

      let extractedData: any[] = [];
      let count = 0;

      if (rawData && typeof rawData === 'object') {
        const hozenData = rawData.hozenallnetdata;
        if (Array.isArray(hozenData)) {
          extractedData = hozenData;
          count = Number(rawData.totalCount || extractedData.length);
        }
        else if (hozenData && typeof hozenData === 'object') {
          if (Array.isArray(hozenData.items)) {
            extractedData = hozenData.items;
          }
          count = Number(hozenData.totalCount || rawData.totalCount || extractedData.length);
        }
      } else if (Array.isArray(rawData)) {
        extractedData = rawData;
        count = rawData.length;
      }

      setResponse(extractedData);
      setTotalCount(count);
    } catch (err: any) {
      console.error('--- Axios詳細エラー ---');
      if (err.response) {
        setError(`サーバーエラー (${err.response.status}): ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        setError('ネットワークエラーまたはCORSポリシーによりブロックされました');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_API_URL]);

  // --------------------------------------------------
  // 🔄 useEffect による自動データ取得
  // --------------------------------------------------
  useEffect(() => {
    handlePostRequest(page, pageSize);
  }, [page, pageSize, handlePostRequest]); // page, pageSize が変わった時も自動実行

  // セル更新時の処理
  const handleCellUpdate = async (row: any, field: string, value: any) => {
    // 1. 直前の状態をバックアップ（失敗時に戻すため）
    const previousResponse = [...response!];

    // 2. とりあえず画面を更新（サクサク感を出す）
    const updatedResponse = response!.map((item) =>
      item.anken_no === row.anken_no ? { ...item, [field]: value } : item
    );
    setResponse(updatedResponse);

    // 3. 非同期でDBを更新
    try {
      const payload = { Process: 'UpdateProjectManagement', anken_no: row.anken_no, field, value };
      const res = await axios.post(BACKEND_API_URL, payload);

      if (res.data.status !== 'success') {
        throw new Error('DB更新失敗');
      }
    } catch (err) {
      // 4. エラーが起きたら元の状態にロールバック
      setResponse(previousResponse);
      notify.error('保存に失敗しました。データを元に戻します。');
    }
  };

  // ページ変更ハンドラ
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // 表示件数変更ハンドラ
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0); // 件数変更時は1ページ目に戻す
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        メンテナンス案件管理（保全・法定）
      </Typography>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px', padding: '10px', border: '1px solid red', borderRadius: '4px' }}>
          <strong>エラー:</strong> {error}
        </div>
      )}

      {loading === false && response === null && !error && (
        <div style={{ color: '#666', fontSize: '12px' }}>
          データを待機中、または解析可能なデータが見つかりませんでした。
        </div>
      )}

      <Box sx={{ height: 'calc(100vh - 180px)', mt: 1 }}>
        <MaintenanceDataTable
          rows={response || []}
          columns={columns}
          loading={loading}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onCellUpdate={handleCellUpdate}
        />
      </Box>

      {response && response.length === 0 && !loading && (
        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
          <p>データが見つかりませんでした（0件、または形式の不一致）。</p>
          <p style={{ fontSize: '12px' }}>※コンソールのログを確認してください。</p>
        </div>
      )}
    </div>
  );
}
